import { useEffect } from "react";
import type { CapturedConsoleError, CapturedNetworkError, PerformanceMetrics } from "./types";

const MAX_CAPTURED = 20;

const capturedConsoleErrors: CapturedConsoleError[] = [];
const capturedNetworkErrors: CapturedNetworkError[] = [];

let listenersInstalled = false;

/**
 * Installs `window.error` + `unhandledrejection` + `console.error` +
 * `window.fetch` interceptors that buffer the last 20 console / network
 * errors. The buffers are read into the feedback POST body so a bug report
 * carries the runtime context that triggered it.
 *
 * Idempotent — multiple call-sites (e.g. mounting the widget on more than
 * one page section) share a single set of listeners.
 */
function installGlobalListeners(): void {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;

  window.addEventListener("error", (e) => {
    capturedConsoleErrors.push({
      message: e.message || String(e.error),
      source: e.filename,
      line: e.lineno,
      col: e.colno,
      timestamp: Date.now(),
    });
    if (capturedConsoleErrors.length > MAX_CAPTURED) capturedConsoleErrors.shift();
  });

  window.addEventListener("unhandledrejection", (e) => {
    capturedConsoleErrors.push({
      message: e.reason?.message || String(e.reason),
      timestamp: Date.now(),
    });
    if (capturedConsoleErrors.length > MAX_CAPTURED) capturedConsoleErrors.shift();
  });

  const origError = console.error;
  console.error = (...args: unknown[]) => {
    capturedConsoleErrors.push({
      message: args.map((a) => (typeof a === "string" ? a : JSON.stringify(a))).join(" "),
      timestamp: Date.now(),
    });
    if (capturedConsoleErrors.length > MAX_CAPTURED) capturedConsoleErrors.shift();
    origError.apply(console, args);
  };

  const origFetch = window.fetch;
  window.fetch = async (...args) => {
    const req = new Request(...args);
    try {
      const res = await origFetch(...args);
      if (!res.ok) {
        capturedNetworkErrors.push({
          url: req.url,
          method: req.method,
          status: res.status,
          statusText: res.statusText,
          timestamp: Date.now(),
        });
        if (capturedNetworkErrors.length > MAX_CAPTURED) capturedNetworkErrors.shift();
      }
      return res;
    } catch (err) {
      capturedNetworkErrors.push({
        url: req.url,
        method: req.method,
        status: null,
        statusText: err instanceof Error ? err.message : "Network error",
        timestamp: Date.now(),
      });
      if (capturedNetworkErrors.length > MAX_CAPTURED) capturedNetworkErrors.shift();
      throw err;
    }
  };
}

export function useGlobalErrorCapture(): void {
  useEffect(() => {
    installGlobalListeners();
  }, []);
}

/** Snapshot accessors — the widget reads these into the POST body. */
export function getCapturedConsoleErrors(): CapturedConsoleError[] {
  return capturedConsoleErrors.slice(-10);
}

export function getCapturedNetworkErrors(): CapturedNetworkError[] {
  return capturedNetworkErrors.slice(-10);
}

export function getPerformanceMetrics(): PerformanceMetrics | null {
  if (typeof window === "undefined" || !window.performance) return null;
  const nav = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!nav) return null;
  return {
    pageLoadMs: Math.round(nav.loadEventEnd - nav.startTime),
    domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd - nav.startTime),
    ttfbMs: Math.round(nav.responseStart - nav.requestStart),
    memoryMB: (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory
      ? Math.round(
          (performance as unknown as { memory: { usedJSHeapSize: number } }).memory.usedJSHeapSize /
            1024 /
            1024,
        )
      : undefined,
  };
}
